import React, { useState, useMemo } from 'react';
import SwitchableDatePicker from './SwitchableDatePicker';
import ConvertedChart from './ConvertedChart';
import { Select, SelectItem, Button, Tabs, Tab, Switch } from '@heroui/react';
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
} from 'recharts';

const formatHour = (h) => {
    if (h === 0 || h === 24) return '12 AM';
    if (h === 12) return '12 PM';
    return h > 12 ? `${h - 12} PM` : `${h} AM`;
};

const formatTimeRange = (h) => {
    return `${formatHour(h)} to ${formatHour(h + 1)}`;
};

const CustomTooltip = ({ active, payload, label, pickerType, pacingType }) => {
    if (active && payload && payload.length) {
        const data = payload[0].payload;
        const tooltipTitle = pickerType === 'date'
            ? formatTimeRange(data.originalIndex)
            : label;

        return (
            <div className="bg-slate-800/95 backdrop-blur-md border border-slate-700/50 rounded-xl p-4 shadow-2xl text-white text-xs w-56 pointer-events-none select-none z-50">
                <div className="font-bold border-b border-slate-600/50 pb-2 mb-2 text-slate-300">
                    {tooltipTitle}
                </div>
                <div className="space-y-2">
                    {/* Primary Mode Indicator */}
                    <div className="text-[9px] text-slate-400 font-semibold uppercase tracking-wider mb-1">
                        {pacingType === 'cumulative' ? 'Cumulative Pacing' : 'Period Pacing'}
                    </div>

                    <div className="flex justify-between items-center">
                        <div className="flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                            <span className="text-slate-400">Total</span>
                        </div>
                        <b className="text-white font-semibold">
                            {pacingType === 'cumulative' ? data.cumulativeTotal : data.totalLeadsUnique}
                        </b>
                    </div>
                    <div className="flex justify-between items-center">
                        <div className="flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                            <span className="text-slate-400">Accepted</span>
                        </div>
                        <div className="flex gap-1 items-end">
                            <b className="text-white font-semibold">
                                {pacingType === 'cumulative' ? data.cumulativeAccepted : data.uniqueAcceptedLeads}
                            </b>
                            <span className="text-[10px] text-emerald-400">
                                ({pacingType === 'cumulative' ? data.cumAcceptedPct : data.acceptedPct}%)
                            </span>
                        </div>
                    </div>
                    <div className="flex justify-between items-center">
                        <div className="flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full bg-rose-500"></span>
                            <span className="text-slate-400">Rejected</span>
                        </div>
                        <div className="flex gap-1 items-end">
                            <b className="text-white font-semibold">
                                {pacingType === 'cumulative' ? data.cumulativeRejected : data.uniqueRejectedLeads}
                            </b>
                            <span className="text-[10px] text-rose-400">
                                ({pacingType === 'cumulative' ? (data.cumulativeTotal > 0 ? Math.round((data.cumulativeRejected / data.cumulativeTotal) * 100) : 0) : data.rejectedPct}%)
                            </span>
                        </div>
                    </div>

                    {/* Secondary Pacing Mode Info */}
                    {pickerType === 'date' && (
                        <div className="border-t border-slate-600/50 pt-2 mt-2 text-[11px]">
                            <div className="flex justify-between items-center gap-2">
                                <span className="text-slate-400">
                                    {pacingType === 'cumulative' ? 'current hour' : 'total leads till now'}
                                </span>
                                <span className={`font-semibold ${pacingType === 'cumulative' ? 'text-blue-400' : 'text-emerald-400'}`}>
                                    {pacingType === 'cumulative'
                                        ? `${data.uniqueAcceptedLeads}/${data.totalLeadsUnique} (${data.acceptedPct}%)`
                                        : `${data.cumulativeAccepted}/${data.cumulativeTotal} (${data.cumAcceptedPct}%)`}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }
    return null;
};

const SkeletonLineChart = () => {
    return (
        <div className="w-full bg-white rounded-2xl p-6 border border-slate-100 shadow-medium">
            <div className="flex justify-between items-center mb-6 animate-pulse">
                <div>
                    <div className="h-8 w-48 bg-slate-200 rounded mb-2"></div>
                    <div className="h-4 w-32 bg-slate-100 rounded"></div>
                </div>
                <div className="flex flex-col gap-2 items-end">
                    <div className="h-9 w-40 bg-slate-200 rounded-lg"></div>
                    <div className="flex gap-4 mt-1">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-slate-200"></div>
                                <div className="h-3 w-12 bg-slate-100 rounded"></div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <div className="flex h-80 w-full relative">
                <div className="flex flex-col justify-between h-full pb-8 pr-4 w-10 animate-pulse">
                    <div className="h-3 w-full bg-slate-100 rounded"></div>
                    <div className="h-3 w-full bg-slate-100 rounded"></div>
                    <div className="h-3 w-full bg-slate-100 rounded"></div>
                </div>
                <div className="flex-1 relative">
                    <div className="h-full w-full border-l border-b border-slate-100 relative">
                        <div className="absolute top-1/2 w-full h-px bg-slate-50"></div>
                        <div className="absolute top-0 w-full h-px bg-slate-50"></div>
                    </div>
                    <div className="absolute bottom-0 left-0 w-full h-6 flex justify-between items-end px-2 mt-2 animate-pulse">
                        {[1, 2, 3, 4, 5, 6].map(i => (
                            <div key={i} className="h-2 w-8 bg-slate-100 rounded"></div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

const LineChart = ({
    loading = false,
    lineChartData,
    selectedLineDate,
    setSelectedLineDate,
    ExecuteLineChartCounts,
    setOemType,
    oemType,
    selectedLineSource,
    setSelectedLineSource,
    sources,
    appliedLineDate,
    useConversionDateRange,
    setUseConversionDateRange
}) => {
    const [pickerType, setPickerType] = useState('date');
    const [pacingType, setPacingType] = useState('current'); // 'current' or 'cumulative'

    const { sortedData, maxVal } = useMemo(() => {
        if (!lineChartData) return { sortedData: [], maxVal: 0 };
        const keys = Object.keys(lineChartData).sort((a, b) => {
            const indexA = parseInt(a.split('_')[1]);
            const indexB = parseInt(b.split('_')[1]);
            return indexA - indexB;
        });

        let data = keys.map(key => {
            const index = parseInt(key.split('_')[1]);
            const raw = lineChartData[key];
            const total = raw.totalLeadsUnique || 0;

            return {
                originalIndex: index,
                totalLeadsUnique: total,
                uniqueAcceptedLeads: raw.uniqueAcceptedLeads || 0,
                uniqueRejectedLeads: raw.uniqueRejectedLeads || 0,
                uniqueConvertedLeads: raw.uniqueConvertedLeads || 0,
                acceptedPct: total > 0 ? ((raw.uniqueAcceptedLeads / total) * 100).toFixed(0) : 0,
                rejectedPct: total > 0 ? ((raw.uniqueRejectedLeads / total) * 100).toFixed(0) : 0,
                convertedPct: total > 0 ? ((raw.uniqueConvertedLeads / total) * 100).toFixed(0) : 0,
                cumulativeTotal: raw.cumulativeTotal || total,
                cumulativeAccepted: raw.cumulativeAccepted || (raw.uniqueAcceptedLeads || 0),
                cumulativeRejected: raw.cumulativeRejected || (raw.uniqueRejectedLeads || 0),
                cumulativeConverted: raw.cumulativeConverted || (raw.uniqueConvertedLeads || 0),
                cumAcceptedPct: raw.cumAcceptedPct || (total > 0 ? Math.round(((raw.uniqueAcceptedLeads || 0) / total) * 100) : 0),
                cumConvertedPct: raw.cumConvertedPct || (total > 0 ? Math.round(((raw.uniqueConvertedLeads || 0) / total) * 100) : 0),
                label: ''
            };
        });

        const rawDate = appliedLineDate?.startDate || appliedLineDate;
        if (rawDate) {
            const selDate = new Date(rawDate);
            const today = new Date();

            const isToday = selDate.getDate() === today.getDate() &&
                selDate.getMonth() === today.getMonth() &&
                selDate.getFullYear() === today.getFullYear();

            if (isToday && data.length > 12) {
                const currentHour = today.getHours();
                data = data.filter(d => d.originalIndex <= currentHour);
            }
        }

        const max = Math.max(...data.map(d => {
            if (pacingType === 'cumulative') {
                return Math.max(
                    d.cumulativeTotal,
                    d.cumulativeAccepted,
                    d.cumulativeRejected
                );
            }

            return Math.max(
                d.totalLeadsUnique,
                d.uniqueAcceptedLeads,
                d.uniqueRejectedLeads
            );
        }));
        const buffer = Math.ceil(max * 0.10); // Changed from 15% to 10% for tighter fit
        const calculatedMax = max + buffer;

        const roundUpMax = (val) => {
            if (val <= 10) return 10;
            const order = Math.pow(10, Math.floor(Math.log10(val)));
            const normalized = val / order;
            let rounded;
            if (normalized <= 1) rounded = 1;
            else if (normalized <= 1.2) rounded = 1.2;
            else if (normalized <= 1.5) rounded = 1.5;
            else if (normalized <= 2) rounded = 2;
            else if (normalized <= 2.5) rounded = 2.5;
            else if (normalized <= 3) rounded = 3;
            else if (normalized <= 4) rounded = 4;
            else if (normalized <= 5) rounded = 5;
            else if (normalized <= 6) rounded = 6;
            else if (normalized <= 7.5) rounded = 7.5;
            else if (normalized <= 8) rounded = 8;
            else rounded = 10;
            return rounded * order;
        };

        const finalMaxVal = calculatedMax === 0 ? 10 : roundUpMax(calculatedMax);

        const count = data.length;
        let labels = [];

        const formatTime = (h) => {
            if (h === 0) return '12 AM';
            if (h === 12) return '12 PM';
            return h > 12 ? `${h - 12} PM` : `${h} AM`;
        };

        const getOrdinal = (n) => {
            const s = ["th", "st", "nd", "rd"];
            const v = n % 100;
            return n + (s[(v - 20) % 10] || s[v] || s[0]);
        };

        const daysOfWeek = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
        const monthsOfYear = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

        if (pickerType === 'custom' && appliedLineDate && appliedLineDate.startDate && appliedLineDate.endDate) {
            const start = new Date(appliedLineDate.startDate);
            const end = new Date(appliedLineDate.endDate);
            const diffDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
            
            if (diffDays <= 31) {
                labels = data.map((d) => {
                    const date = new Date(start.getTime() + (d.originalIndex * 24 * 60 * 60 * 1000));
                    return `${monthsOfYear[date.getMonth()]} ${date.getDate()}`;
                });
            } else {
                labels = data.map((d) => {
                    const date = new Date(start);
                    date.setMonth(start.getMonth() + d.originalIndex);
                    return `${monthsOfYear[date.getMonth()]} ${date.getFullYear()}`;
                });
            }
        }
        else if (pickerType === 'date') {
            labels = data.map((d) => formatTime(d.originalIndex));
        }
        else if (count <= 7) {
            labels = data.map((_, i) => daysOfWeek[i % 7]);
        }
        else if (count <= 12) labels = data.map((_, i) => monthsOfYear[i % 12]);
        else if (count <= 24) labels = data.map((d) => formatTime(d.originalIndex));
        else labels = data.map((_, i) => getOrdinal(i + 1));

        const finalData = data.map((d, i) => ({ ...d, label: labels[i] }));
        return { sortedData: finalData, maxVal: finalMaxVal };
    }, [lineChartData, appliedLineDate, pickerType, pacingType]);

    const activeTotalKey = pacingType === 'cumulative' ? 'cumulativeTotal' : 'totalLeadsUnique';
    const activeAcceptedKey = pacingType === 'cumulative' ? 'cumulativeAccepted' : 'uniqueAcceptedLeads';
    const activeRejectedKey = pacingType === 'cumulative' ? 'cumulativeRejected' : 'uniqueRejectedLeads';

    if (loading) {
        return <SkeletonLineChart />;
    }

    return (
        <div className="">
            <div className="w-full bg-white rounded-2xl p-6 border border-slate-100 shadow-medium">
                <div className="flex flex-col lg:flex-row justify-between items-start mb-6 gap-4">
                    <div>
                        <h2 className="text-2xl font-bold text-slate-800">Leads Performance Overview</h2>
                        <p className="text-slate-500 text-sm">Leads sent vs Accepted vs Rejected</p>
                    </div>

                    <div className='flex flex-col gap-4 items-end w-full lg:w-auto'>
                        <div className='flex flex-wrap md:flex-nowrap gap-2 md:gap-4 w-full lg:w-auto justify-end items-center'>
                            <Select
                                selectedKeys={[String(selectedLineSource)]}
                                onChange={e => setSelectedLineSource(e.target.value)}
                                placeholder='Select source'
                                variant="bordered"
                                className="min-w-[180px] w-full md:w-auto"
                            >
                                {sources.map((source) => (
                                    <SelectItem key={source.key}>{source.label}</SelectItem>
                                ))}
                            </Select>

                            <Select
                                className='min-w-[120px] w-full md:w-auto'
                                variant='bordered'
                                placeholder='Select OEM'
                                selectedKeys={[String(oemType)]}
                                onChange={(e) => {
                                    setOemType(e.target.value);
                                }}
                            >
                                <SelectItem key="1">Tvs</SelectItem>
                                <SelectItem key="2">Ola</SelectItem>
                                <SelectItem key="3">Ampere</SelectItem>
                                <SelectItem key="4">Revolt</SelectItem>
                                <SelectItem key="5">Simple</SelectItem>
                            </Select>

                            <div className="w-full md:w-auto">
                                <SwitchableDatePicker
                                    selectedLineDate={selectedLineDate}
                                    setSelectedLineDate={setSelectedLineDate}
                                    pickerType={pickerType}
                                    setPickerType={setPickerType}
                                />
                            </div>

                            <Button
                                className="bg-[#0085DB] text-white font-semibold rounded-lg w-full md:w-auto min-w-[80px]"
                                onPress={() => ExecuteLineChartCounts(selectedLineDate, useConversionDateRange)}
                                isLoading={loading}
                            >
                                Apply
                            </Button>
                        </div>


                        {/* Pacing Toggle Segmented Control */}
                        <div className="w-full md:w-auto flex justify-center">
                            <Tabs
                                aria-label="Pacing options"
                                color="default"
                                variant="bordered"
                                size="md"
                                selectedKey={pacingType}
                                onSelectionChange={setPacingType}
                            >
                                <Tab key="current" title="Current Period" />
                                <Tab key="cumulative" title="Till Period" />
                            </Tabs>
                        </div>

                        <div className="flex gap-4 text-xs font-semibold mt-1">
                            <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-blue-500"></span> Total</div>
                            <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-emerald-500"></span> Accepted</div>
                            <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-rose-500"></span> Rejected</div>
                        </div>
                    </div>
                </div>

                <div className="w-full h-80 mt-2 z-10 relative">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                            data={sortedData}
                            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                        >
                            <defs>
                                <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.15} />
                                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.01} />
                                </linearGradient>
                                <linearGradient id="colorAccepted" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.1} />
                                    <stop offset="95%" stopColor="#10b981" stopOpacity={0.01} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                            <XAxis
                                dataKey="label"
                                tickLine={false}
                                axisLine={false}
                                tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 500 }}
                                dy={10}
                            />
                            <YAxis
                                tickLine={false}
                                axisLine={false}
                                tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 500 }}
                                dx={-5}
                                domain={[0, maxVal]}
                            />
                            <RechartsTooltip
                                content={<CustomTooltip pickerType={pickerType} pacingType={pacingType} />}
                                cursor={{ stroke: '#cbd5e1', strokeWidth: 1, strokeDasharray: '4 4' }}
                                wrapperStyle={{ zIndex: 100 }}
                            />
                             <Area
                                type="monotone"
                                dataKey={activeTotalKey}
                                stroke="#3b82f6"
                                strokeWidth={2.5}
                                fillOpacity={1}
                                fill="url(#colorTotal)"
                                activeDot={{ r: 6, strokeWidth: 0 }}
                            />
                            <Area
                                type="monotone"
                                dataKey={activeAcceptedKey}
                                stroke="#10b981"
                                strokeWidth={2}
                                fillOpacity={1}
                                fill="url(#colorAccepted)"
                                activeDot={{ r: 5, strokeWidth: 0 }}
                            />
                            <Area
                                type="monotone"
                                dataKey={activeRejectedKey}
                                stroke="#f43f5e"
                                strokeWidth={2}
                                fillOpacity={0}
                                strokeDasharray="3 3"
                                activeDot={{ r: 5, strokeWidth: 0 }}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Converted Chart */}
            <ConvertedChart
                data={sortedData}
                pickerType={pickerType}
                pacingType={pacingType}
                useConversionDateRange={useConversionDateRange}
                setUseConversionDateRange={setUseConversionDateRange}
                ExecuteLineChartCounts={ExecuteLineChartCounts}
                selectedLineDate={selectedLineDate}
            />
        </div>
    );
};

export default LineChart;